import React from "react";
import { useFormContext } from "react-hook-form";
import useSetFormData from "repoV2/features/Common/modules/FormData/utils/useSetFormData";
import { FORM_KEYS } from "repoV2/features/Common/modules/FormData/FormData.constants";
import { IAppliedCouponCodeDetails } from "repoV2/features/Common/modules/FormData/interfaces/IAppliedCouponCodeDetails";
import { checkIsObjectEmpty } from "repoV2/utils/common/dataTypes/object";
import useSetIsPaymentProcessing from "./useSetIsPaymentProcessing";
import { useCheckoutBasicDetailsContext } from "../modules/CheckoutBasicDetailsContextProvider";
import {
    applied_coupon_form_key,
    default_applied_coupon_code_details,
} from "../modules/PaymentDetailsSection/modules/FormGroups/modules/CouponCodeFormGroup/CouponCodeFormGroup.constants";

const useStateInitBFCache = () => {
    const { listingUuid } = useCheckoutBasicDetailsContext();
    const { setIsPaymentProcessing } = useSetIsPaymentProcessing();
    const { setFormData } = useSetFormData();
    const { getValues, reset } = useFormContext();

    React.useEffect(() => {
        /**
         *  Scenerio: User makes payment clicks on pay button, then is redirected to a payment gateway.
         * User then uses browser back button to come to this page. Here, browser caching doesn't work so we
         * reset it
         */
        const initialFormData = getValues();
        const appliedCoupon: IAppliedCouponCodeDetails =
            initialFormData?.[applied_coupon_form_key] ||
            default_applied_coupon_code_details;

        if (appliedCoupon.applied) {
            setFormData(
                `${FORM_KEYS.listing_details}.${listingUuid}.${FORM_KEYS.applied_coupon_code_details}`,
                { ...appliedCoupon }
            );
        }
        if (!checkIsObjectEmpty(initialFormData)) {
            reset({
                ...initialFormData,
            });
        }

        setIsPaymentProcessing(false);

        /**
         * Scenerio: User makes payment clicks on pay button, then is redirected to a payment gateway.
         * User then uses browser back button to come to this page. Because of chrome's bf caching,
         * the page does not load again and old data is persisted, so the loader keeps on loading,
         * if this is the scenerio, we set the loader false
         */
        // Function to handle state initialization if the page is restored from bfcache
        const handleStateInitIfBFCache = (event: PageTransitionEvent) => {
            if (event.persisted) {
                const data = getValues() || {};
                // Incase we have a coupon in for already filled, we need to re apply it.
                // TODO: Please note this will not be required to do in fitire as we plan to
                // remove the form redux for checkout pages and use only react-hook-form state
                const alreadyAppliedCoupon: IAppliedCouponCodeDetails =
                    data?.[applied_coupon_form_key] ||
                    default_applied_coupon_code_details;
                if (alreadyAppliedCoupon.applied) {
                    setFormData(
                        `${FORM_KEYS.listing_details}.${listingUuid}.${FORM_KEYS.applied_coupon_code_details}`,
                        { ...alreadyAppliedCoupon }
                    );
                }
                setIsPaymentProcessing(false);
                reset({
                    ...data,
                });
            }
        };

        /**
         * We are using 'pageshow' event here to check if our page is being cached, if so
         * we re fill the form with the data
         * For more info on theis event - https://web.dev/articles/bfcache#observe-page-restored
         */
        // Add event listeners for pageshow and pagehide
        window.addEventListener("pageshow", handleStateInitIfBFCache);

        // Cleanup event listeners on component unmount
        return () => {
            window.removeEventListener("pageshow", handleStateInitIfBFCache);
        };
    }, []);
};

export default useStateInitBFCache;
